import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBackward } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GlobalOverlay from 'components/GlobalOverlay';
import { head } from 'lodash';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Tooltip } from '../ui';

const LayoutWrapper = styled.div`
    height: 100vh;
    display: flex;
`;

const NavbarWrapper = styled.nav`
    width: 50px;
    height: 100%;
    background-color: #f4f4f5;

    display: flex;
    flex-direction: column;

    .logo {
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;

        margin-bottom: 1rem;
    }

    .tab-bar {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
    }

    .tab {
        width: 50px;
        margin: 2rem 0;
        color: rgb(164, 167, 181);
        transition: color 200ms;
        font-size: 1.15rem;

        // center icon

        svg {
            display: block;
            margin: auto;
        }
    }

    .tab:hover {
        color: rgb(114, 117, 131);
    }

    .tab.is-active {
        color: ${(props) => props.theme.colors.green};
    }
`;

const TabBar = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;

    > * {
        margin: 2rem 0;
    }
`;

const ContentWrapper = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;

    .header {
        padding: 0 1rem;
        min-height: 50px;
        width: 100%;
        background-color: #f4f4f5;

        display: flex;
        align-items: center;
        justify-content: space-between;

        h1  {
            font-size: 1.25rem;
            font-weight: ${(props) => props.theme.fontWeightMedium};
        }

        h1,
        h5  {
            margin: 0;
        }
    }

    .header-buttons {
        display: flex;
    }

    .content {
        flex: 1 1 auto;
        overflow: auto;
        height: 100%;

        .container-fluid {
            height: 100%;
            overflow: scroll;
        }
    }
`;

const Tab = styled.a<{ isActive?: boolean; disabled?: boolean }>`
    width: 50px;
    cursor: pointer;
    color: ${(p) => (p.disabled ? 'rgb(207, 207, 207)' : p.isActive ? p.theme.colors.green : 'rgb(164, 167, 181)')};
    transition: color 200ms;
    font-size: 1.15rem;

    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};

    // center icon

    svg {
        display: block;
        margin: auto;
    }

    &:hover {
        color: rgb(114, 117, 131);
    }
`;

export interface Tab {
    tab: string;
    title: string;
    content: string | React.ReactNode;
    icon: IconProp;
    disabled?: boolean;
    shallow?: boolean;
}

export interface ModuleLayoutProps {
    tabs: Tab[];
    headerButtons?: Array<string & React.ReactNode>;
    header?: string;
    clubSlug?: string;
    pageTitlePrefix?: string;
    tabTitle?: boolean;
    contentClassName?: string;
    initialIndex?: number;
}

export const ModuleLayout = ({
    pageTitlePrefix,
    tabs,
    headerButtons,
    header,
    clubSlug,
    tabTitle = false,
    initialIndex = 0,
    contentClassName
}: ModuleLayoutProps) => {
    const { query, asPath, ...router } = useRouter();
    const currentTab = (Array.isArray(query.tab) ? head(query.tab) : query.tab)?.trim().toLowerCase();
    const isActive = (tab = '') => (!currentTab && tab === '') || tab === currentTab;

    const getCurrentComponent = () => {
        let component = tabs.find(({ tab }) => {
            if (!currentTab) {
                return !tab || tab === '';
            } else {
                return tab === currentTab;
            }
        });

        return component || tabs[initialIndex];
    };

    return (
        <>
            {tabTitle && (
                <Head>
                    <title>{`${pageTitlePrefix || 'Sportision -'} ${getCurrentComponent().title}`}</title>
                </Head>
            )}
            <GlobalOverlay />
            <LayoutWrapper>
                <NavbarWrapper>
                    <Link href="/" className="logo">
                        <svg viewBox="0 0 1000 1000" color="white" version="1.1" width={30} height={30}>
                            <g transform="matrix(6,0,0,1,0,0)">
                                <g transform="matrix(0.166667,0,0,1,151.411,788.454)">
                                    <path
                                        d="M0,-576.908L-14.137,-488.197L-866.972,-488.197C-893.66,-427.024 -908.467,-359.472 -908.467,-288.454C-908.467,-12.308 -684.614,211.546 -408.467,211.546C-330.786,211.546 -257.242,193.827 -191.663,162.216L-874.961,-120.929L-842.745,-305.207L-271.869,-77.115L-253.827,-182.784L-779.601,-392.836L-32.178,-392.836L-123.145,122.19C6.61,31.881 91.533,-118.365 91.533,-288.454C91.533,-395.89 57.654,-495.4 0,-576.908M-83.647,-668.583L-733.273,-668.583C-645.902,-743.325 -532.449,-788.454 -408.467,-788.454C-284.472,-788.454 -171.019,-743.325 -83.647,-668.583"
                                        style={{ fillRule: 'nonzero' }}
                                    />
                                </g>
                            </g>
                        </svg>
                    </Link>
                    <TabBar>
                        {tabs.map((tab, index) => (
                            <Link
                                key={tab.tab}
                                href={{ pathname: router.pathname, query: { ...query, tab: tab.tab } }}
                                shallow={tab.shallow === undefined || tab.shallow}
                            >
                                <Tab
                                    isActive={(!currentTab && index === 0) || isActive(tab.tab)}
                                    disabled={tab.disabled}
                                >
                                    <Tooltip placement="right" tooltip={tab.title}>
                                        <div>
                                            <FontAwesomeIcon icon={tab.icon} />
                                        </div>
                                    </Tooltip>
                                </Tab>
                            </Link>
                        ))}
                        <Link href={clubSlug ? `/club/${clubSlug}` : '/home'}>
                            <Tab className="mt-auto">
                                <Tooltip placement="right" tooltip={clubSlug ? 'Zurück zum Verein' : 'Zur Startseite'}>
                                    <div>
                                        <FontAwesomeIcon icon={faBackward} />
                                    </div>
                                </Tooltip>
                            </Tab>
                        </Link>
                    </TabBar>
                </NavbarWrapper>
                <ContentWrapper>
                    <div className="header">
                        {header || <h1>{getCurrentComponent().title}</h1>}
                        <div className="header-buttons">{headerButtons?.map((btn) => btn)}</div>
                    </div>
                    <div className="content">
                        <div className={'container-fluid' + (contentClassName ? ' ' + contentClassName : '')}>
                            {getCurrentComponent().content}
                        </div>
                    </div>
                </ContentWrapper>
            </LayoutWrapper>
        </>
    );
};
